import { useCallback, useMemo, useRef, useState } from 'react';

export interface StateRefObject<T> {
  getState: () => T;
  setState: (state: T) => void;
}

/**
 * Sometimes you need a ref to the latest value of state, for example in callbacks.
 * This hook works like useState, but also returns a ref to the state that is always in sync with the state.
 */
export const useStateRef = <T>(initialState: T | (() => T)): StateRefObject<T> => {
  const [state, setState] = useState<T>(initialState);
  const ref = useRef<T>(state);
  const dispatch = useCallback((state: T) => {
    ref.current = state;
    setState(state);
  }, []);
  const refObject = useMemo(
    () => ({
      getState: () => ref.current,
      setState: dispatch,
    }),
    [ref, dispatch]
  );
  return refObject;
};
